// Friends, blocks, and user reports (App Store Guideline 1.2).
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/** Add a friend by uid or @username (reciprocal). */
export async function addFriend(input: { userId?: string; username?: string }): Promise<{ ok: true; friendId: string }> {
  const call = httpsCallable<typeof input, { ok: true; friendId: string }>(functions, 'addFriend');
  return (await call(input)).data;
}

/** Remove a friend (reciprocal). */
export async function removeFriend(userId: string): Promise<{ ok: true }> {
  const call = httpsCallable<{ userId: string }, { ok: true }>(functions, 'removeFriend');
  return (await call({ userId })).data;
}

/** A player the signed-in user has blocked (one doc in users/{uid}/blocks). */
export interface BlockedUser {
  userId: string;
  displayName?: string | null;
  username?: string | null;
  createdAt: number;
}

/**
 * Block a player. Severs any friendship, cancels pending duels between the two, and
 * prevents either side from friending or challenging the other until unblocked.
 */
export async function blockUser(input: { userId?: string; username?: string }): Promise<{ ok: true; blockedUserId: string }> {
  const call = httpsCallable<typeof input, { ok: true; blockedUserId: string }>(functions, 'blockUser');
  return (await call(input)).data;
}

/** Lift a block. */
export async function unblockUser(userId: string): Promise<{ ok: true }> {
  const call = httpsCallable<{ userId: string }, { ok: true }>(functions, 'unblockUser');
  return (await call({ userId })).data;
}

/** Report a player for objectionable content or behaviour. */
export interface ReportUserInput {
  userId?: string;
  username?: string;
  reason?: 'harassment' | 'hate_speech' | 'inappropriate_name' | 'spam' | 'cheating' | 'impersonation' | 'other';
  details?: string;
  context?: string;
}
export async function reportUser(input: ReportUserInput): Promise<{ ok: true }> {
  const call = httpsCallable<ReportUserInput, { ok: true }>(functions, 'reportUser');
  return (await call(input)).data;
}

/** List the players the signed-in user has blocked. */
export async function listBlockedUsers(): Promise<{ ok: true; blocked: BlockedUser[] }> {
  const call = httpsCallable<Record<string, never>, { ok: true; blocked: BlockedUser[] }>(functions, 'listBlockedUsers');
  return (await call({})).data;
}
